import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import Modal from 'react-modal';
import BurstMode from '@material-ui/icons/BurstMode';
import Lock from '@material-ui/icons/Lock';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import FanClubStore from 'stores/FanClubStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import { ENUM_PROFILE_GALLERY_TYPE } from 'pages/Settings/Profile/Galleries/config';
import LightBoxComponent from 'components/LightBoxComponent/LightBoxComponent';
import SFWOverlay from 'components/SFWOverlay/SFWOverlay';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import APIStates from 'enum/APIStates';
import allUserGroups from 'enum/userGroups';
import trackingService from 'services/UITrackingService';

import GalleryStore from './GalleryStore';
import styles from './GalleryThumbnail.css';


const GalleryThumbnail = ({ gallery, user, autoStartGallery, history }) => {
  const { getPhotos, lightboxPhotos, photosState, deletePhoto, deleteGallery } = GalleryStore;
  const { isFan } = FanClubStore;
  const { profileName, disableSwipe } = ProfileStore;
  const { username, galleryName, imageCount, mainImage, galleryId, galleryType } = gallery;

  const [isLightBoxOpen, setLightBoxOpen] = useState(false);
  const [activeGalleryId, setActiveGalleryId] = useState('');
  const [photoIndex, setPhotoIndex] = useState(0);

  const isMyProfile = user.username && profileName.toLowerCase() === user.username.toLowerCase();
  const isSfw = user.user.getIn(['user', 'sfw']) && galleryType !== ENUM_PROFILE_GALLERY_TYPE.SAFE_FOR_WORK;
  const isLastImageInGallery = lightboxPhotos.length === 1;
  const isProfileOrSfwGallery =
    galleryType !== ENUM_PROFILE_GALLERY_TYPE.PROFILE && galleryType !== ENUM_PROFILE_GALLERY_TYPE.SAFE_FOR_WORK;
  const isAdmin = user.accessLevel >= ACCESS_LEVEL_NUM.ADMIN;
  const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
  const loaded = photosState === APIStates.LOADED;
  const useFullSizeThumbnails = UserStore.hasCF(allUserGroups.PROFILES_V2_FULL_SIZE_GALLERY_THUMBS);

  const trackGalleryActions = useCallback((action, contextual = {}, details = {}) => {
    const trackingEvent = 'profileGalleryInteraction';

    const displayedImage = details?.displayedImage;

    const other = {};
    if (displayedImage !== undefined) {
      other.displayedImage = { id: displayedImage.imageId };
    }

    trackingService.emit(trackingEvent, {
      action,
      tag: {
        user: username,
        gallery: {
          name: galleryName,
          type: galleryType,
          id: galleryId,
          imageCount,
          thumbnail: { imageId: mainImage?.imageId },
        },
        ...contextual,
        ...other,
      },
    });
  }, [galleryId, galleryName, galleryType, imageCount, mainImage, username]);

  const handleThumbnailClick = useCallback(() => {
    if (isSfw) {
      return;
    }
    if (isGuestBilling && gallery.isLocked) {
      ModalService.showModal(ModalService.MODAL_KEYS.GUEST_BILLING_MODAL);

      return;
    }
    if (gallery.isLocked && !isFan && !isMyProfile && !isAdmin) {
      ModalService.showModal(ModalService.MODAL_KEYS.JOIN_FAN_CLUB_MODAL);
    } else {
      disableSwipe(true);
      setActiveGalleryId(gallery.galleryId);
      getPhotos(profileName, gallery.galleryId);
      trackGalleryActions('open');
    }
  }, [
    disableSwipe,
    gallery.galleryId,
    gallery.isLocked,
    getPhotos,
    isAdmin,
    isFan,
    isGuestBilling,
    isMyProfile,
    isSfw,
    profileName,
    trackGalleryActions,
  ]);

  useEffect(() => Modal.setAppElement('#app'), []);
  useEffect(() => {
    const clearHistoryState = () => {
      if (history.location && history.location.state && history.location.state.openProfileGallery) {
        const state = { ...history.location.state };
        delete state.openProfileGallery;
        history.replace({ ...history.location, state });
      }
    };
    if (autoStartGallery) {
      handleThumbnailClick();
      clearHistoryState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history && history.location && history.location.state]);

  useEffect(() => {
    if (photosState === APIStates.LOADED &&
        activeGalleryId === galleryId &&
        lightboxPhotos.length > 0) {
      setLightBoxOpen(true);
    }
  }, [photosState, activeGalleryId, galleryId, lightboxPhotos.length]);

  const showNextImage = useCallback(() => {
    const newPhotoIndex = photoIndex + 1;

    setPhotoIndex((newPhotoIndex) % imageCount);
    trackGalleryActions('scroll', { next: 1 }, { displayedImage: lightboxPhotos[newPhotoIndex] });
  }, [
    photoIndex,
    imageCount,
    lightboxPhotos,
    setPhotoIndex,
    trackGalleryActions,
  ]);

  const showPrevImage = useCallback(() => {
    const newPhotoIndex = photoIndex - 1;

    setPhotoIndex((newPhotoIndex + imageCount) % imageCount);
    trackGalleryActions('scroll', { prev: 1 }, { displayedImage: lightboxPhotos[newPhotoIndex] });
  }, [
    imageCount,
    lightboxPhotos,
    photoIndex,
    setPhotoIndex,
    trackGalleryActions,
  ]);

  const handleCloseLightBox = useCallback(() => {
    setLightBoxOpen(false);
    setActiveGalleryId('');
    disableSwipe(false);
    setPhotoIndex(0);

    trackGalleryActions('close');
  }, [
    setLightBoxOpen,
    setActiveGalleryId,
    disableSwipe,
    setPhotoIndex,
    trackGalleryActions,
  ]);

  const onImageDeleteSubmit = useCallback(() => {
    const { imageId } = lightboxPhotos[photoIndex];
    const isLastPictureInArray = !isLastImageInGallery && photoIndex + 1 === lightboxPhotos.length;
    const nextImageId = !isLastImageInGallery && lightboxPhotos.filter((photo) => photo.imageId !== imageId)[0].imageId;
    if (isLastImageInGallery && isProfileOrSfwGallery) {
      deleteGallery(profileName, galleryId);
    } else {
      deletePhoto(profileName, galleryId, imageId, nextImageId, galleryName, galleryType);
    }
    if (isLastPictureInArray) {
      showPrevImage();
    }

    trackGalleryActions('imageDelete');
  }, [
    deleteGallery,
    deletePhoto,
    galleryId,
    galleryName,
    galleryType,
    isLastImageInGallery,
    isProfileOrSfwGallery,
    lightboxPhotos,
    photoIndex,
    profileName,
    showPrevImage,
    trackGalleryActions,
  ]);

  const handleOpenDeleteModal = useCallback(() => {
    const isLastImage = isLastImageInGallery && isProfileOrSfwGallery;
    ModalService.showModal(ModalService.MODAL_KEYS.DELETE_MODAL, {
      onClose: ModalService.hideModal,
      show: true,
      onSubmit: onImageDeleteSubmit,
      content:
        isLastImage ? translate('account.galleries.deleteLastImage') : translate('account.galleries.deleteImage'),
      rootClass: styles.wrapperDelete,
    });

    trackGalleryActions('openDeleteModal');
  }, [isLastImageInGallery, isProfileOrSfwGallery, onImageDeleteSubmit, trackGalleryActions]);

  const previewImgClass = classNames({
    [styles.previewImg]: true,
    [styles.previewImgFull]: useFullSizeThumbnails,
    [styles.placeholderImage]: !mainImage,
  });

  const coverImageStyles = useMemo(() => {
    if (!mainImage) {
      return undefined;
    }

    const { imageUrl, previewUrl } = mainImage;
    const bgImage = useFullSizeThumbnails ? imageUrl : previewUrl;

    return {
      backgroundImage: `url(${bgImage})`,
      filter: gallery.isLocked && !isAdmin ? 'blur(4px)' : 'none',
    };
  }, [gallery.isLocked, isAdmin, mainImage, useFullSizeThumbnails]);

  return (
    <Fragment>
      <div className={styles.GalleryThumbnail} onClick={handleThumbnailClick}>
        {isSfw && <SFWOverlay />}
        <div className={styles.galleryImage}>
          {gallery.isFanGallery && (
            <span className={styles.fansOnly}>{translate('v2profile.galleries.fans.only')}</span>
          )}
          <div
            className={previewImgClass}
            style={coverImageStyles}
          />
          <div className={styles.tint}>
            <div className={loaded ? styles.fadeOut : ''} />
            <div className={styles.galleryInfo}>
              <div className={styles.galleryIcon}>
                {gallery.isLocked && !isMyProfile && !isAdmin && <Lock className={styles.lockedIcon} />}
                <BurstMode className={styles.photoIcon} />
              </div>
              <div className={styles.imageCount}>
                {imageCount} {translate('v2profile.gallery.images.label')}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.galleryName}>{galleryName}</div>
      </div>
      {isLightBoxOpen && (
        <LightBoxComponent
          accessLevel={user.accessLevel}
          currentIndex={photoIndex}
          deletePic={handleOpenDeleteModal}
          gotoNext={showNextImage}
          gotoPrevious={showPrevImage}
          imageCaption={
            <div className={styles.caption}>
              <span className={styles.galleryName}>{galleryName}</span>
              <span className={styles.photoNumber}>
                {photoIndex + 1} of {imageCount}
              </span>
            </div>
          }
          images={lightboxPhotos}
          isOpen={isLightBoxOpen}
          onClose={handleCloseLightBox}
        />
      )}
    </Fragment>
  );
};

GalleryThumbnail.propTypes = {
  gallery: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  autoStartGallery: PropTypes.bool,
  history: PropTypes.object,
};

GalleryThumbnail.defaultProps = { autoStartGallery: false, history: null };

export default observer(GalleryThumbnail);
